import { ShieldHalf } from "lucide-react";
import { LegalCompanyInfo } from "@/components/LegalCompanyInfo";
import { useTosVersion } from "@/components/TermsBody";
import { useReleaseDateLabel } from "@/hooks/useReleaseMetadata";

/**
 * Compliance Roadmap Item 7 — Privacy Policy at `/legal/privacy`.
 *
 * Sections required by the compliance spec: data collected, purposes,
 * providers, retention, user rights, contact, security, sale/share
 * disclosure, under-21 policy, international users. All placeholders
 * ({{RETENTION_WINDOW}}, {{PROCESSOR_LIST}}, etc.) must be filled in by
 * the Operator + attorney before publish — nothing is invented here.
 */

function S({ n, title, id, children }) {
  return (
    <section id={id} className="space-y-2.5">
      <h2 className="font-display text-lg text-white tracking-[0.04em] flex gap-2.5">
        <span className="text-cyan-300 font-mono">{n}.</span> {title}
      </h2>
      <div className="text-white/65 text-[13.5px] leading-relaxed space-y-2.5">{children}</div>
    </section>
  );
}

const Em = ({ children }) => <span className="text-white font-medium">{children}</span>;
const Placeholder = ({ children }) => (
  <span className="font-mono text-amber-200/90 bg-amber-500/[0.06] border border-amber-400/20 rounded px-1.5 py-0.5 text-[12px]">{children}</span>
);

export default function PrivacyPage() {
  const version = useTosVersion();
  const releaseDate = useReleaseDateLabel();
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="privacy-page">
      <div className="hud-pill rounded-2xl p-6 sm:p-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-11 w-11 rounded-xl bg-emerald-400/15 border border-emerald-300/30 flex items-center justify-center shrink-0">
            <ShieldHalf className="h-5 w-5 text-emerald-300" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-emerald-300/80">Legal · Data &amp; Privacy</div>
            <h1 className="font-display text-3xl sm:text-4xl text-white tracking-[0.05em]">PRIVACY POLICY</h1>
          </div>
        </div>
        <p data-testid="privacy-current-version" className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 mb-8">
          Playden Inc. · Version {version} · {releaseDate}
        </p>

        <div className="space-y-8" data-testid="privacy-document">
          <div className="rounded-xl border border-emerald-400/25 bg-emerald-500/[0.05] p-4 text-[13px] leading-relaxed text-emerald-100/90">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-emerald-300/90 mb-1">Summary</div>
            This policy describes what personal information Playden Inc. (<Em>&quot;Playden,&quot; &quot;we,&quot; &quot;us&quot;</Em>) collects when
            you use the Service, why we collect it, who we share it with, how long we keep it, and the rights you have
            regarding your data. Playden is a closed-loop, 21+ platform. If you are under 21, you are not permitted to
            use the Service and must not submit any information to us.
          </div>

          <S n="1" title="Information we collect" id="data-collected">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><Em>Account data</Em> — email, display name, hashed password, account role, credit balance, activity history, date of birth, IP address, device fingerprint, browser and hardware characteristics captured at each Terms acceptance.</li>
              <li><Em>Content you submit</Em> — cards, prompts, uploads, trade offers, chat messages, Community Prediction Pool commitments, and any files you attach (including 3D print files).</li>
              <li><Em>Payment metadata</Em> — the amount, timestamp, and processor reference of every top-up. We do not store full card numbers; cryptocurrency addresses and transaction hashes are stored for auditability. Manual Venmo top-ups store the note text and admin verification record.</li>
              <li><Em>Cookies &amp; local storage</Em> — session tokens, gate-acceptance flags, and UI preferences (chat opacity, theme). We do not use third-party advertising trackers.</li>
              <li><Em>Server logs</Em> — request path, status, latency, and error traces, retained for operational and fraud-prevention purposes.</li>
            </ul>
          </S>

          <S n="2" title="Why we collect it (purposes of processing)" id="purposes">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Operate the Service — deliver the features you request, mint your cards, resolve matches, process top-ups.</li>
              <li>Verify eligibility — enforce the 21+ requirement, prevent duplicate accounts, and record consent to the Terms.</li>
              <li>Prevent fraud, abuse, cheating, chargeback fraud, and platform manipulation.</li>
              <li>Improve the Service — analyze feature usage in aggregate; investigate bugs.</li>
              <li>Meet legal obligations, respond to lawful requests, and enforce our Terms.</li>
            </ul>
          </S>

          <S n="3" title="Service providers we share data with" id="providers">
            <p>We share the minimum necessary personal data with the following categories of providers. The current exact list is:</p>
            <Placeholder>{"{{PROCESSOR_LIST}}"}</Placeholder>
            <p>Typical categories include: cloud hosting, database and file-storage vendors, cryptocurrency payment processors, email delivery, transactional SMS (if enabled), analytics (aggregate, no cross-site tracking), and LLM / image-generation APIs used to power features you invoke. Each provider is bound by contractual data-protection terms.</p>
          </S>

          <S n="4" title="How long we keep your data (retention)" id="retention">
            <p>Retention windows differ by data category. Current windows:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Account data: <Placeholder>{"{{RETENTION_ACCOUNT}}"}</Placeholder> after account closure.</li>
              <li>Terms-acceptance forensic records (IP, fingerprint, timestamp): <Placeholder>{"{{RETENTION_TOS}}"}</Placeholder>.</li>
              <li>Payment metadata: <Placeholder>{"{{RETENTION_PAYMENTS}}"}</Placeholder> for tax and audit compliance.</li>
              <li>Chat, trade, and match history: <Placeholder>{"{{RETENTION_ACTIVITY}}"}</Placeholder>.</li>
              <li>Server logs: <Placeholder>{"{{RETENTION_LOGS}}"}</Placeholder>.</li>
            </ul>
          </S>

          <S n="5" title="Your rights" id="rights">
            <p>Depending on where you live, you may have the right to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Access a copy of the personal data we hold about you.</li>
              <li>Correct inaccurate data.</li>
              <li>Delete your account and associated personal data (subject to legal retention obligations).</li>
              <li>Object to or restrict certain processing.</li>
              <li>Port your data to another service where technically feasible.</li>
              <li>Opt out of any sale or sharing of personal information — see Section 8 below.</li>
              <li>Lodge a complaint with your local data-protection authority.</li>
            </ul>
            <p>Requests can be submitted through the contact addresses in Section 10.</p>
          </S>

          <S n="6" title="Security" id="security">
            <p>We hash passwords with bcrypt, store login PINs as separately-hashed values, transport all traffic over TLS, restrict administrative access, and enforce rate limits + IP-ban logic on authentication endpoints. No system is perfect; we cannot guarantee absolute security. You are responsible for keeping your credentials confidential.</p>
          </S>

          <S n="7" title="Under-21 policy" id="under-21">
            <p>Playden is exclusively for individuals aged 21 or older. We do not knowingly collect personal information from anyone under 21. Registration requires a date of birth and the server rejects any registration where age &lt; 21. If we learn that we have received personal information from someone under 21, we will delete the account and the associated personal data.</p>
          </S>

          <S n="8" title="Sale or sharing of personal information" id="sale-share">
            <p>Playden <Em>does not sell</Em> personal information for monetary consideration. Playden does not share personal information for cross-context behavioral advertising. Where an applicable state law (e.g., CCPA/CPRA, VA CDPA, CO CPA) defines &quot;sharing&quot; more broadly, our disclosures to service providers listed in Section 3 for the purposes listed in Section 2 are the only such transfers, and each is bound by contract to use the data only for the disclosed purpose.</p>
          </S>

          <S n="9" title="International users" id="international">
            <p>The Service is operated from <Placeholder>{"{{GOVERNING_LAW_STATE}}"}</Placeholder>. If you access the Service from outside that jurisdiction, you consent to the transfer of your personal data to it. Where cross-border transfers require additional safeguards (e.g., GDPR standard contractual clauses), those safeguards are in place with the providers listed in Section 3. Playden does not currently offer the Service in <Placeholder>{"{{BLOCKED_JURISDICTIONS}}"}</Placeholder>.</p>
          </S>

          <S n="10" title="Contact" id="contact">
            <p>Questions or requests concerning this Privacy Policy may be sent through the Service&apos;s in-app support channel or to the addresses in the Company &amp; Contact block below.</p>
          </S>

          <S n="11" title="Changes to this Policy" id="changes">
            <p>We may update this Policy from time to time. When we do, we will post the updated document with a new effective date. Material changes will be surfaced in-Service.</p>
          </S>

          <LegalCompanyInfo className="mt-4" testid="privacy-company-info" />

          <div className="pt-2 border-t border-white/10">
            <p className="text-white/40 font-mono text-[11px] uppercase tracking-[0.25em]">
              Playden Inc. · Privacy Policy · Version {version} · {releaseDate}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
